import {
  Close,
  Edit,
} from '@mui/icons-material';
import React, {
  useEffect,
  useState,
} from 'react';

import {
  Box,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useNotification } from '../../../../hooks/NotificationContext';
import {
  formatCommentWithAccountIds,
  formatCommentWithDisplayNames,
  getDateAndTime,
  renderCommentBody,
} from '../../../../lib/utils';
import {
  deleteComment,
  updateComment,
} from '../../../../modules/entity-details';
import CustomAvatar from '../../CustomAvatar';
import CommentInput from './CommentInput';
import { generateNotificationUrl } from '../../../../lib/notificationUtils';
import { getDisplayUserName } from '../../../../lib/userUtils';

import ActionMenu from '../../../../ui/primitives/ActionMenu';
import ActionButton from '../../../../ui/primitives/ActionButton';
import ConfirmationModal from '../../../../ui/primitives/ConfirmationModal';
import { useUser } from '../../../../context/UserContext';

const SingleComment = ({
  comment,
  fetchData,
  users,
  entityKey,
  otherData,
}) => {
  const { currentUser } = useUser();

  const commentDate =
    getDateAndTime(
      comment.updated_at,
    );

  const [showConfirm, setShowConfirm] =
    useState(false);

  const [isEditing, setIsEditing] =
    useState(false);

  const [editedComment, setEditedComment] =
    useState(comment.body);

  const [loading, setLoading] =
    useState(false);

  const [taggedUsers, setTaggedUsers] =
    useState(
      comment?.mentions || [],
    );

  const { addNotification } =
    useNotification();

  const isEdited = () =>
    new Date(
      comment.updated_at,
    ).getTime() >
    new Date(
      comment.created_at,
    ).getTime();

  const deleteSingleComment =
    async () => {
      setLoading(true);

      try {
        await deleteComment(
          comment.id,
        );

        fetchData();

        addNotification(
          'success',
          'Comment deleted successfully!',
        );
      } catch {
        addNotification(
          'danger',
          'Failed to delete comment.',
        );
      } finally {
        setLoading(false);
      }
    };

  const updateSingleComment =
    async (newComment) => {
      setLoading(true);

      const mentions = [
        ...new Set(taggedUsers),
      ];

      const commentBody =
        formatCommentWithAccountIds(
          newComment,
          users,
        );

      try {
        let additionalData = {};

        const {
          mentions:
          existingMentions = [],
          user,
          id: commentId,
          gainss_project_id:
          projectId,
          entity_type:
          entityType,
        } = comment;

        const newMentions =
          mentions.filter(
            (userId) =>
              !existingMentions.includes(
                userId,
              ),
          );

        if (
          newMentions &&
          newMentions.length > 0
        ) {
          const notificationLink =
            generateNotificationUrl(
              entityType,
              projectId,
              entityKey,
              'comments',
              otherData,
            ) || '#';

          additionalData = {
            commentorName:
              getDisplayUserName(
                user,
              ),
            entityKey,
            entityLink:
              notificationLink,
            newMentions,
          };
        }

        const payload = {
          body: commentBody.trim(),
          mentions,
          additionalData,
        };

        await updateComment(
          commentId,
          {
            comment: payload,
          },
        );

        setIsEditing(false);

        fetchData();

        addNotification(
          'success',
          'Comment updated successfully!',
        );
      } catch (error) {
        addNotification(
          'danger',
          error.message ||
          'Failed to update comment.',
        );
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    setEditedComment(
      formatCommentWithDisplayNames(
        comment.body,
        users,
      ),
    );
  }, [comment, users]);

  return (
    <>
      <Box>
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
        >
          <CustomAvatar
            firstName={
              comment.user?.first_name
            }
            lastName={
              comment.user?.last_name
            }
            showTitle={false}
            size={32}
          />

          <Box sx={{ flex: 1 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Typography variant="body2">
                <strong>
                  {getDisplayUserName(
                    comment?.user,
                  )}
                </strong>{' '}
                has added a comment{' '}
                {isEdited()
                  ? '(Edited)'
                  : ''}{' '}
                - {commentDate}
              </Typography>

              {currentUser?.id === comment?.user?.id && (
                <ActionMenu
                  tooltip="Comment actions"
                  items={[
                    {
                      label: isEditing
                        ? 'Close editor'
                        : 'Edit comment',
                      icon: <Edit />,
                      onClick: () =>
                        setIsEditing((prev) => !prev),
                    },
                    {
                      label: 'Delete comment',
                      icon: <Close />,
                      color: 'error',
                      onClick: () => setShowConfirm(true),
                    },
                  ]}
                />
              )}
            </Stack>

            <Box sx={{ mt: 1.5 }}>
              {isEditing ? (
                <CommentInput
                  users={users}
                  onCommentChange={
                    setEditedComment
                  }
                  comment={
                    editedComment
                  }
                  setTaggedUsers={
                    setTaggedUsers
                  }
                />
              ) : (
                <Typography variant="body2">
                  {renderCommentBody(
                    comment.body,
                    users,
                  )}
                </Typography>
              )}
            </Box>

            {isEditing && (
              <Stack
                direction="row"
                spacing={1}
                justifyContent="flex-end"
                sx={{ mt: 2 }}
              >
                <ActionButton
                  label="Save"
                  intent="primary"
                  size="small"
                  loading={loading}
                  disabled={
                    !editedComment
                  }
                  onClick={() =>
                    updateSingleComment(
                      editedComment,
                    )
                  }
                />

                <ActionButton
                  label="Cancel"
                  intent="secondary"
                  size="small"
                  onClick={() =>
                    setIsEditing(
                      false,
                    )
                  }
                />
              </Stack>
            )}
          </Box>
        </Stack>

        <Divider sx={{ mt: 2 }} />
      </Box>

      <ConfirmationModal
        show={showConfirm}
        handleConfirm={
          deleteSingleComment
        }
        handleClose={() =>
          setShowConfirm(false)
        }
        title="Remove comment"
        body="Are you sure you want to remove this comment?"
        loading={loading}
      />
    </>
  );
};

export default SingleComment;